import { Col, Div, Row } from '@pinpoint/design-kit';
import { charts } from '@pinpoint/onpoint-charts';
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import arraySupport from 'dayjs/plugin/arraySupport';

import CONSTANTS from 'src/store/constants';
import { formatNumberV2 } from 'src/functions';

import { LabeledCountPanel } from './panel';
import { ApiResponseProp } from '../../store/typings';

dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(arraySupport);

const today = dayjs.utc();

function aggregateByPredicate(array, predicate, target) {
  return array.map((label) => {
    const entry = target.find(predicate(label));
    const value = Math.round((entry?.metric_metadata?.duration?.location?.duration?.['50%'] || 0) * 1000) || null;
    return value;
  });
}

function getP50(rttEntries, metric) {
  const entries = rttEntries.filter((v) => v.metric_name === metric);
  if (entries.length === 0) {
    return 0;
  }
  let entry = entries[entries.length - 1];
  if (metric === 'WEEK_TO_DATE' && dayjs.utc().day() === 1 && entries.length > 1) {
    entry = entries[entries.length - 2];
  }
  return Math.round((entry?.metric_metadata?.duration?.location?.duration?.['50%'] ?? 0) * 1000);
}

export function ApiReturnTimeCardDefault({
  client,
  generateStatsStatus,
  rttEntries,
  rttSecondaryEntries,
  rttToDateEntries,
  usageFilter,
}) {
  const [stackedTheme, setStackedTheme] = useState('pinpoint1');

  const rttWtd = useMemo(() => getP50(rttToDateEntries, 'WEEK_TO_DATE'), [rttToDateEntries]);
  const rttMtd = useMemo(() => getP50(rttToDateEntries, 'MONTH_TO_DATE'), [rttToDateEntries]);
  const rttYtd = useMemo(() => getP50(rttToDateEntries, 'YEAR_TO_DATE'), [rttToDateEntries]);

  const chartData = useMemo(() => {
    const data = {
      title: `Return Times Over ${CONSTANTS.REAL_TIME_API.USAGE.FILTERS_DESC[usageFilter]}`,
      series: [],
    };
    if (!client?.billingDay) return data;
    const defaultDays = CONSTANTS.REAL_TIME_API.USAGE.FILTERS_LABEL_LENGTH[usageFilter];
    const maskPrefix = CONSTANTS.REAL_TIME_API.USAGE.FILTERS_MASK[usageFilter];
    data.labels = Array(defaultDays)
      .fill(0)
      .map((_, idx) => today.subtract(idx, 'day').format('YYYY-MM-DD'))
      .reverse();
    let predicate = (label) => (entry) => dayjs.utc(entry.start_timestamp * 1000).format('YYYY-MM-DD') === label;
    if (
      [
        CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_3_BILLING_CYCLES,
        CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_6_BILLING_CYCLES,
        CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_12_BILLING_CYCLES,
      ].includes(usageFilter)
    ) {
      const billingCycleEnd = dayjs.utc([dayjs.utc().year(), dayjs.utc().month(), +client.billingDay]);
      const currBillingCycleStart = billingCycleEnd.isBefore(today)
        ? billingCycleEnd
        : billingCycleEnd.subtract(1, 'M');

      data.labels = Array(defaultDays)
        .fill(0)
        .map((_, idx) => currBillingCycleStart.subtract(idx, 'M').format('MMM DD YYYY'))
        .reverse();
      data.labels[defaultDays - 1] = 'Cur';

      predicate = (label) => (entry) => {
        const entryPeriod = dayjs.utc(entry.start_timestamp * 1000);
        // Handle current cycle entry
        if (entryPeriod.isSame(currBillingCycleStart) && label === 'Cur') return true;

        // Handle past cycle entry
        const labelPeriodStart = dayjs.utc(
          `${label.split(' ')[0]} ${client.billingDay} ${label.split(' ')[2]}`,
          `MMM-D-YYYY`
        );
        return entryPeriod.isSame(labelPeriodStart);
      };
    }

    data.series.push({
      name: 'Median Milliseconds',
      values: aggregateByPredicate(data.labels, predicate, rttSecondaryEntries),
    });

    if (['3c', '6c', '12c'].includes(maskPrefix))
      data.labels = data.labels.map((label) => (label !== 'Cur' ? label.slice(0, -4) : label));
    return data;
  }, [usageFilter, client, rttSecondaryEntries]);

  const chartOptions = useMemo(() => {
    const options = {
      grid: {
        top: '10%',
        bottom: 112,
      },
      xAxis: {
        name: [
          CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_3_BILLING_CYCLES,
          CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_6_BILLING_CYCLES,
          CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_12_BILLING_CYCLES,
        ].includes(usageFilter)
          ? 'Billing cycle beginning on'
          : 'Period',
        axisLabel: {
          show: true,
          interval: 'auto',
        },
      },
      yAxis: {
        name: 'P50 (ms)',
      },
      tooltip: {
        show: true,
        formatter: ({ data }) => `${formatNumberV2(data.value)} ms`,
      },
      title: {
        top: 0,
      },
      legend: {
        itemGap: 24,
      },
      useUTC: true,
    };
    if (chartData.series.length >= 1)
      options.series = [
        {
          itemStyle: {
            color: '#FFBA00',
          },
        },
      ];
    if (chartData.series.length > 2)
      options.series.push({
        itemStyle: {
          color: '#FF671D',
        },
      });
    if (chartData.series.length === 2 || chartData.series.length >= 3)
      options.series.push({
        itemStyle: {
          color: '#A0A0A0',
        },
      });
    return options;
  }, [chartData, usageFilter]);

  return (
    <Row p={{ t: '.75rem', x: '2.5rem', b: '2rem' }} flexGrow="1">
      <Col size="2" d="flex" flexDir="column" justify="flex-start" align="center" gutterWidth="0">
        <Row m={{ b: '1rem' }} w="100%">
          <LabeledCountPanel
            status={generateStatsStatus}
            value={rttYtd ? `${rttYtd} ms` : '-'}
            title="Year to Date"
            panelProps={{
              p: '1.5rem',
              w: '100%',
            }}
            countProps={{
              textWeight: '500',
              textSize: 'display1',
              textColor: 'black',
            }}
            titleProps={{
              textWeight: '500',
              textSize: 'body',
              textColor: 'marigold',
            }}
          />
        </Row>
        <Row m={{ b: '1rem' }} w="100%">
          <LabeledCountPanel
            status={generateStatsStatus}
            value={rttMtd ? `${rttMtd} ms` : '-'}
            title="Month to Date"
            panelProps={{
              p: '1.5rem',
              w: '100%',
            }}
            countProps={{
              textWeight: '500',
              textColor: 'black',
              textSize: 'display1',
            }}
            titleProps={{
              textWeight: '500',
              textSize: 'body',
              textColor: 'marigold',
            }}
          />
        </Row>
        <Row w="100%">
          <LabeledCountPanel
            status={generateStatsStatus}
            value={rttWtd ? `${rttWtd} ms` : '-'}
            title="Week to Date"
            panelProps={{
              p: '1.5rem',
              w: '100%',
            }}
            countProps={{
              textWeight: '500',
              textColor: 'black',
              textSize: 'display1',
            }}
            titleProps={{
              textWeight: '500',
              textSize: 'body',
              textColor: 'marigold',
            }}
          />
        </Row>
      </Col>
      <Col size="10" d="flex" align="stretch" justify="stretch" gutterWidth="0">
        <Row w="100%" h="100%" m={{ l: '1rem' }}>
          <Col gutterWidth="0" border="1px solid" borderColor="shadeD9" rounded="md" size="12">
            <Div p={{ t: '.5rem', r: '.5rem' }} m={{ b: '-2rem' }} h="100%">
              {!!chartData?.series?.length && (
                <charts.VerticalBar
                  {...chartData}
                  formatter={(params) => formatNumberV2(params.value)}
                  options={chartOptions}
                  theme={stackedTheme}
                  onThemeChange={setStackedTheme}
                  usePalette
                  showPalette={false}
                  markLine={false}
                />
              )}
              <Div textAlign="right" textSize="caption" m={{ t: '.5rem' }}>
                Statistics aggregated daily
              </Div>
            </Div>
          </Col>
        </Row>
      </Col>
    </Row>
  );
}

ApiReturnTimeCardDefault.defaultProps = {
  generateStatsStatus: null,
};

ApiReturnTimeCardDefault.propTypes = {
  client: PropTypes.shape({
    billingDay: PropTypes.number.isRequired,
    enableRealTimeAPICaching: PropTypes.bool.isRequired,
  }).isRequired,
  // component
  usageFilter: PropTypes.string.isRequired,
  // rtApiUsage store
  generateStatsStatus: ApiResponseProp,
  rttEntries: PropTypes.arrayOf(
    PropTypes.shape({
      period: PropTypes.string,
      count: PropTypes.number,
      meta: PropTypes.string,
    })
  ).isRequired,
  rttSecondaryEntries: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  rttToDateEntries: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};
